import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Clock5, FileText, LockKeyhole, TimerOff } from "lucide-react";

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { getConfidentialTimeLeft } from "@/utils/confidential-mode";

const remainingMilliseconds = (expiresAt: string) =>
  Math.max(0, new Date(expiresAt).getTime() - Date.now());

export function ConfidentialSessionIndicator({
  expiresAt,
  onExpire,
}: {
  expiresAt: string;
  onExpire: () => void;
}) {
  const { t } = useTranslation();
  const [remaining, setRemaining] = useState(() =>
    remainingMilliseconds(expiresAt),
  );

  useEffect(() => {
    const updateRemaining = () => {
      const next = remainingMilliseconds(expiresAt);
      setRemaining(next);
      if (next === 0) onExpire();
    };

    updateRemaining();
    const interval = window.setInterval(updateRemaining, 1_000);
    document.addEventListener("visibilitychange", updateRemaining);
    window.addEventListener("focus", updateRemaining);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", updateRemaining);
      window.removeEventListener("focus", updateRemaining);
    };
  }, [expiresAt, onExpire]);

  const timeLeft = useMemo(
    () =>
      getConfidentialTimeLeft(
        expiresAt,
        new Date(expiresAt).getTime() - remaining,
      ),
    [expiresAt, remaining],
  );

  return (
    <HoverCard openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          className="flex h-10 min-w-[261px] cursor-default items-center justify-start gap-2 rounded-lg border border-[var(--confidential-control-border)] bg-[var(--confidential-control-background)] px-4 py-[7.5px] text-sm font-medium leading-[1.5] tracking-[0.07px] text-[var(--warning)] shadow-sm outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={t("playground.confidentialSessionDetails")}
        >
          <LockKeyhole className="size-4" />
          <span>{t("playground.confidentialMode")}</span>
          <Clock5 className="size-4" />
          <span className="tabular-nums" aria-live="off">
            <span aria-hidden="true">• </span>
            {t("playground.timeLeft", { time: timeLeft })}
          </span>
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        align="end"
        sideOffset={5}
        className="w-[281px] rounded-lg border-border bg-card p-0.5 text-sm font-normal leading-[1.5] tracking-[0.07px] shadow-md"
      >
        <div className="p-2">
          <div className="flex min-h-8 items-center gap-2 rounded-md px-2 py-[5.5px]">
            <LockKeyhole className="size-5 text-[var(--warning)]" />
            <span>{t("playground.noMemoryWrites")}</span>
          </div>
          <div className="flex min-h-8 items-center gap-2 rounded-md px-2 py-[5.5px]">
            <TimerOff className="size-5 text-[var(--warning)]" />
            <span>{t("playground.notSavedToHistory")}</span>
          </div>
          <div className="flex min-h-8 items-center gap-2 rounded-md px-2 py-[5.5px]">
            <FileText className="size-5 text-muted-foreground" />
            <span>{t("playground.documentsRemainReadable")}</span>
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
